import { useRef, type KeyboardEvent } from "react";

import { t, type Language, type StringKey } from "../lib/i18n";

export type TabId = "overview" | "invoices" | "atividade" | "trust";

/** Glance-first tab order (per the F37 §12.6 IA: Overview · Invoices ·
 * Atividade · Trust). The bar wraps / scrolls horizontally on narrow widths
 * (~400-600px Teams-tab). The operator-flavoured Diagnostics tab was dropped
 * in the §12 redesign (Task 7): its exceptions fold into the Needs-you card,
 * its version/heartbeat into the status-strip support popover. Kept
 * module-internal (not exported) so this file only exports a component + a
 * type — react-refresh stays happy. */
const TAB_ORDER: ReadonlyArray<{ id: TabId; labelKey: StringKey }> = [
  { id: "overview", labelKey: "tab.overview" },
  { id: "invoices", labelKey: "tab.invoices" },
  { id: "atividade", labelKey: "tab.atividade" },
  { id: "trust", labelKey: "tab.trust" },
];

interface TabBarProps {
  active: TabId;
  onSelect: (id: TabId) => void;
  language: Language;
}

/** Presentational tab strip — `role="tablist"` with one `role="tab"`
 * button per tab. No router dependency (tabs are local state, not
 * routes). The active tab is `kwanza-accent`; inactive tabs are muted
 * `kwanza-inkSoft`.
 *
 * Implements the WAI-ARIA APG Tabs keyboard pattern (A2 — was missing):
 * roving tabindex (only the active tab is in the tab order), Arrow keys
 * move + activate (automatic activation, matching the click behaviour),
 * Home/End jump to the ends. The global `:focus-visible` ring
 * (`tailwind.css`) makes the moved focus visible. `min-h-[44px]` meets
 * the touch-target floor on Teams mobile. */
export function TabBar({ active, onSelect, language }: TabBarProps): JSX.Element {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  const focusTab = (index: number): void => {
    const clamped = (index + TAB_ORDER.length) % TAB_ORDER.length;
    onSelect(TAB_ORDER[clamped].id);
    refs.current[clamped]?.focus();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLButtonElement>, index: number): void => {
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        event.preventDefault();
        focusTab(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        event.preventDefault();
        focusTab(index - 1);
        break;
      case "Home":
        event.preventDefault();
        focusTab(0);
        break;
      case "End":
        event.preventDefault();
        focusTab(TAB_ORDER.length - 1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      role="tablist"
      aria-label={t("app.subtitle", language)}
      className="flex flex-wrap gap-1 overflow-x-auto border-b border-kwanza-divider"
    >
      {TAB_ORDER.map(({ id, labelKey }, index) => {
        const selected = active === id;
        return (
          <button
            key={id}
            ref={(el) => {
              refs.current[index] = el;
            }}
            type="button"
            role="tab"
            id={`tab-${id}`}
            aria-selected={selected}
            aria-controls={`tabpanel-${id}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onSelect(id)}
            onKeyDown={(e) => onKeyDown(e, index)}
            data-testid={`tab-${id}`}
            className={
              // The active tab keeps the mint underline — a non-text
              // indicator, which is the right job for that hue — and moves
              // the LABEL to `accentText`. Mint as text on white is 2.3:1
              // (design review finding 1).
              "-mb-px inline-flex min-h-[44px] items-center whitespace-nowrap border-b-2 px-4 py-2 text-sm " +
              (selected
                ? "border-kwanza-accent font-bold text-kwanza-accentText"
                : "border-transparent font-medium text-kwanza-inkSoft hover:text-kwanza-ink")
            }
          >
            {t(labelKey, language)}
          </button>
        );
      })}
    </div>
  );
}
